// ============================================================
// ANSICHTSSACHE-AGENT – Hell, Dunkel, System
// ============================================================
// Ein Knopf, drei Zustaende: jeder Klick schaltet weiter —
// hell, dunkel, wie im System, wieder hell.
//
// Der dritte Zustand ist der Grund fuer den Umbau: „wie im
// System“ liess sich vorher nur herstellen, indem man nie
// anfasste, was im Speicher stand — einmal umgeschaltet, gab es
// keinen Weg zurueck.
//
// Das Zeichen zeigt den ZUSTAND, nicht den naechsten Schritt:
// Sonne heisst hell, Mond heisst dunkel, Bildschirm heisst „wie
// im System“. Damit niemand raten muss, welche der beiden
// Lesarten gilt, nennt der Hinweis beides — wo man ist und was
// der naechste Klick tut.
//
// Gesetzt wird das Thema vom Skript in app/layout.tsx, bevor das
// erste Bild steht. Dieser Knopf liest nur ab und schreibt beim
// Weiterschalten zurueck.
//
// Gelesen wird ueber `useSyncExternalStore` und nicht ueber einen
// Effekt mit `setState`. Das ist nicht nur regelkonform, es ist
// auch richtiger: der Knopf haengt damit am Attribut selbst.
// Aendert es jemand anders — ein zweiter Knopf, die
// Entwicklerkonsole, ein anderer Reiter — zieht die Anzeige mit,
// statt stehenzubleiben.
//
// Der dritte Parameter ist die Antwort fuer den Server, der kein
// `document` hat: dort gilt die Vorgabe.
// ============================================================

"use client";

import { useSyncExternalStore } from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  THEME_ATTRIBUTE,
  THEME_FALLBACK,
  applyThemeMode,
  readThemeMode,
  type ThemeMode,
} from "@/lib/theme";

function subscribe(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [THEME_ATTRIBUTE],
  });
  return () => observer.disconnect();
}

/** Die Runde, die der Knopf dreht — und zugleich die Zeichen dazu. */
const RUNDE: { mode: ThemeMode; label: string; Icon: typeof Sun }[] = [
  { mode: "light", label: "Hell", Icon: Sun },
  { mode: "dark", label: "Dunkel", Icon: Moon },
  { mode: "system", label: "Wie im System", Icon: Monitor },
];

export function ThemeSwitch({ className }: { className?: string }) {
  const mode = useSyncExternalStore(subscribe, readThemeMode, () => THEME_FALLBACK);

  const stelle = Math.max(
    0,
    RUNDE.findIndex((s) => s.mode === mode),
  );
  const jetzt = RUNDE[stelle];
  const naechste = RUNDE[(stelle + 1) % RUNDE.length];
  const Icon = jetzt.Icon;

  return (
    <button
      type="button"
      onClick={() => applyThemeMode(naechste.mode)}
      aria-label={`Ansicht: ${jetzt.label}. Weiter zu: ${naechste.label}`}
      title={`Ansicht: ${jetzt.label} — weiter zu ${naechste.label}`}
      className={cn(
        "text-text-secondary grid h-9 w-9 place-items-center rounded-lg",
        "duration-fast ease-standard hover:bg-bg-tertiary hover:text-text-primary transition-colors",
        className,
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}
